import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Popover,
  List,
  ListItemIcon,
  ListItemText,
  Box,
  ListItemButton,
  Divider,
  Avatar,
  Button,
  CircularProgress,
  ListItemAvatar,
  Backdrop, // Added Backdrop import
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HistoryIcon from "@mui/icons-material/History";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserProvider";
import useApiEp from "../hooks/useApiEp";
import AccountSelector from "./AccountSelector";
import { useAccount } from "../context/AccountContext";

const formatTitle = (segment) => {
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function for rgba conversion
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex?.slice(1, 3), 16);
  const g = parseInt(hex?.slice(3, 5), 16);
  const b = parseInt(hex?.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const AppBarComponent = ({
  isMobile,
  open,
  drawerWidth,
  collapsedWidth,
  handleDrawerToggle,
}) => {
  const location = useLocation();
  const lastSegment = location.pathname.split("/").filter(Boolean).pop();

  const title = lastSegment ? formatTitle(lastSegment) : "Home";
  // Added loggingOut from UserContext
  const { user, loading, loggingOut, logoutUser } = useContext(UserContext);
  const { data, isLoading } = useApiEp("/api/notifications");
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const { selectedAccount, setSelectedAccount } = useAccount();

  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const isNotifOpen = Boolean(notifAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };
  const profileMenuId = "primary-search-account-menu";
  const notifMenuId = "notifications-menu";

  // Color palette for light theme
  const colors = {
    primary: "#4f46e5",
    secondary: "#7c3aed",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    background: "#f8fafc",
    cardBg: "#ffffff",
    textPrimary: "#1e293b",
    textSecondary: "#64748b",
    accent: "#8b5cf6",
  };

  const renderProfileMenu = (
    <Popover
      anchorEl={profileAnchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={profileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isProfileMenuOpen}
      onClose={handleProfileMenuClose}
      PaperProps={{
        sx: {
          maxWidth: 320,
          mt: 1.5,
          minWidth: 240,
          borderRadius: "16px",
          background: `
            linear-gradient(135deg, 
              rgba(255, 255, 255, 0.95) 0%, 
              rgba(248, 250, 252, 0.9) 100%
            )
          `,
          backdropFilter: "blur(20px)",
          boxShadow: `
            0 20px 40px rgba(0,0,0,0.1),
            0 8px 24px rgba(99, 102, 241, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8)
          `,
          border: "1px solid rgba(255, 255, 255, 0.4)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, ${colors.success})`,
            backgroundSize: "300% 300%",
            animation: "gradientEdge 6s ease infinite",
          },
        },
      }}
    >
      {/* User Profile Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          background:
            "linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(124, 58, 237, 0.03) 100%)",
          borderBottom: "1px solid rgba(99, 102, 241, 0.1)",
        }}
      >
        <Avatar
          src={user?.profileImage}
          alt={user?.name}
          sx={{
            width: 48,
            height: 48,
            mr: 2,
            border: "2px solid rgba(79, 70, 229, 0.2)",
            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
            boxShadow: "0 4px 12px rgba(79, 70, 229, 0.2)",
          }}
        >
          {!user?.profileImage && (user?.name?.[0] || "")}
        </Avatar>
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color={colors.textPrimary}
          >
            {user?.name || "User"}
          </Typography>
          <Typography variant="body2" color={colors.textSecondary}>
            {user?.email || "user@example.com"}
          </Typography>
        </Box>
      </Box>

      {/* Menu Items */}
      <List sx={{ p: 1, background: "transparent" }}>
        {[
          {
            icon: (
              <PersonIcon fontSize="small" sx={{ color: colors.primary }} />
            ),
            text: "Profile",
            path: "/home/profile",
            color: colors.primary,
          },
          {
            icon: <HistoryIcon fontSize="small" sx={{ color: colors.info }} />,
            text: "Usage History",
            path: "/home/transaction-history",
            color: colors.info,
          },
          {
            icon: (
              <SettingsIcon fontSize="small" sx={{ color: colors.warning }} />
            ),
            text: "Settings",
            path: "/home/settings",
            color: colors.warning,
          },
          {
            icon: <LogoutIcon fontSize="small" sx={{ color: colors.error }} />,
            text: "Sign Out",
            onClick: logoutUser,
            color: colors.error,
          },
        ].map((item) => (
          <ListItemButton
            key={item.text}
            {...(item.path
              ? { component: Link, to: item.path }
              : { component: "div" })}
            onClick={() => {
              handleProfileMenuClose();
              if (item.onClick) item.onClick();
            }}
            sx={{
              borderRadius: "12px",
              px: 2,
              py: 1.25,
              my: 0.25,
              transition: "all 0.3s ease",
              background: "transparent",
              "&:hover": {
                background: `linear-gradient(135deg, ${item.color}10 0%, ${item.color}05 100%)`,
                transform: "translateX(4px)",
                boxShadow: `0 4px 12px ${hexToRgba(item.color, 0.15)}`,
                "& .MuiListItemIcon-root": {
                  transform: "scale(1.1)",
                },
              },
              "&.Mui-selected": {
                background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}08 100%)`,
                borderLeft: `3px solid ${item.color}`,
                "& .MuiTypography-root": {
                  fontWeight: 600,
                  color: item.color,
                },
              },
            }}
            selected={item.path ? location.pathname === item.path : false}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
                color: item.color,
                transition: "all 0.3s ease",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                variant: "body1",
                fontWeight: 500,
                color:
                  item.path && location.pathname === item.path
                    ? item.color
                    : colors.textPrimary,
                transition: "color 0.3s ease",
              }}
            />
            {item.path && location.pathname === item.path && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}80 100%)`,
                  ml: 1,
                  boxShadow: `0 2px 4px ${hexToRgba(item.color, 0.3)}`,
                }}
              />
            )}
          </ListItemButton>
        ))}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid rgba(99, 102, 241, 0.1)",
          background:
            "linear-gradient(135deg, rgba(79, 70, 229, 0.03) 0%, rgba(124, 58, 237, 0.02) 100%)",
        }}
      >
        <Typography variant="caption" color={colors.textSecondary}>
          v{process.env.REACT_APP_VERSION || "1.0.0"}
        </Typography>
      </Box>
    </Popover>
  );

  const renderNotifications = (
    <Popover
      anchorEl={notifAnchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      id={notifMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isNotifOpen}
      onClose={handleNotifClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          width: { xs: "90vw", sm: 400 },
          maxHeight: "70vh",
          borderRadius: "16px",
          background: `
            linear-gradient(135deg, 
              rgba(255, 255, 255, 0.95) 0%, 
              rgba(248, 250, 252, 0.9) 100%
            )
          `,
          backdropFilter: "blur(20px)",
          boxShadow: `
            0 20px 40px rgba(0,0,0,0.1),
            0 8px 24px rgba(99, 102, 241, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8)
          `,
          border: "1px solid rgba(255, 255, 255, 0.4)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: `linear-gradient(90deg, ${colors.info}, ${colors.accent}, ${colors.primary})`,
            backgroundSize: "300% 300%",
            animation: "gradientEdge 6s ease infinite",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background:
            "linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(124, 58, 237, 0.03) 100%)",
          borderBottom: "1px solid rgba(99, 102, 241, 0.1)",
        }}
      >
        <Typography variant="h6" fontWeight={600} color={colors.textPrimary}>
          Notifications
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ overflowY: "auto", maxHeight: "calc(70vh - 64px)" }}>
        {isLoading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress
              size={32}
              sx={{
                color: colors.primary,
                "& .MuiCircularProgress-circle": {
                  strokeWidth: 3,
                },
              }}
            />
            <Typography
              variant="body2"
              color={colors.textSecondary}
              sx={{ mt: 1.5 }}
            >
              Loading notifications...
            </Typography>
          </Box>
        ) : data && data.length > 0 ? (
          <List dense sx={{ p: 0 }}>
            {data.map((notification, index) => (
              <React.Fragment key={index}>
                <ListItemButton
                  sx={{
                    px: 2,
                    py: 1.75,
                    transition: "all 0.3s ease",
                    background: "transparent",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(124, 58, 237, 0.05) 100%)",
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44, mr: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        background:
                          "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                        color: "#ffffff",
                        boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
                      }}
                    >
                      <NotificationsActiveIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color={colors.textPrimary}
                        sx={{ mb: 0.5 }}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color={colors.textSecondary}
                          component="span"
                          sx={{ display: "block", lineHeight: 1.4 }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={colors.textSecondary}
                          display="block"
                          sx={{ mt: 1, opacity: 0.7 }}
                        >
                          {formatDate(notification.createdOn)}
                        </Typography>
                      </>
                    }
                    sx={{ my: 0.5 }}
                  />
                </ListItemButton>
                {index < data.length - 1 && (
                  <Divider sx={{ borderColor: "rgba(99, 102, 241, 0.08)" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)",
                borderRadius: "50%",
                boxShadow: "0 4px 12px rgba(79, 70, 229, 0.1)",
              }}
            >
              <NotificationsOffIcon
                sx={{ color: colors.textSecondary, fontSize: "2rem" }}
              />
            </Box>
            <Typography
              variant="body1"
              color={colors.textPrimary}
              sx={{ mb: 0.5, fontWeight: 500 }}
            >
              No new notifications
            </Typography>
            <Typography variant="body2" color={colors.textSecondary}>
              You're all caught up
            </Typography>
          </Box>
        )}
      </Box>

      {data?.length > 5 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: "1px solid rgba(99, 102, 241, 0.1)",
            background:
              "linear-gradient(135deg, rgba(79, 70, 229, 0.03) 0%, rgba(124, 58, 237, 0.02) 100%)",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color={colors.textSecondary}>
            Showing {data.length} notifications
          </Typography>
        </Box>
      )}
    </Popover>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: `calc(100% - ${
            isMobile ? 0 : open ? drawerWidth : collapsedWidth
          }px)`,
          ml: isMobile ? 0 : `${open ? drawerWidth : collapsedWidth}px`,
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          background: `
            linear-gradient(135deg, 
              rgba(255, 255, 255, 0.95) 0%, 
              rgba(248, 250, 252, 0.9) 100%
            )
          `,
          backdropFilter: "blur(20px)",
          boxShadow: `
            0 4px 20px rgba(0,0,0,0.08),
            0 2px 8px rgba(99, 102, 241, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.8)
          `,
          borderBottom: "1px solid rgba(255, 255, 255, 0.4)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, ${colors.success}, ${colors.warning})`,
            backgroundSize: "400% 400%",
            animation: "gradientFlow 8s ease infinite",
          },
          "@keyframes gradientFlow": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important" }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                color: colors.textPrimary,
                background: "rgba(79, 70, 229, 0.05)",
                "&:hover": {
                  background: "rgba(79, 70, 229, 0.1)",
                  transform: "rotate(90deg)",
                },
                transition: "all 0.3s ease",
                borderRadius: "12px",
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}
          >
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.5px",
                background:
                  "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #c026d3 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline-block",
              }}
            >
              {title}
            </Typography>

            {["home", "dashboard", "chats"].includes(lastSegment) && (
              <AccountSelector
                selectedAccount={selectedAccount}
                setSelectedAccount={setSelectedAccount}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="large"
              aria-label="show notifications"
              color="inherit"
              onClick={handleNotifOpen}
              sx={{
                position: "relative",
                color: colors.textPrimary,
                background: isNotifOpen
                  ? "linear-gradient(135deg, rgba(79, 70, 229, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)"
                  : "rgba(79, 70, 229, 0.05)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, rgba(79, 70, 229, 0.2) 0%, rgba(124, 58, 237, 0.15) 100%)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.3s ease",
                borderRadius: "12px",
                boxShadow: isNotifOpen
                  ? "0 4px 12px rgba(79, 70, 229, 0.2)"
                  : "none",
              }}
            >
              <Badge
                badgeContent={data?.length || 0}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    right: 5,
                    top: 5,
                    border: "2px solid #ffffff",
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    fontWeight: 600,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <Button
              color="inherit"
              onClick={handleProfileMenuOpen}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                textTransform: "none",
                px: 1.5,
                minWidth: "auto",
                borderRadius: "16px",
                background: isProfileMenuOpen
                  ? "linear-gradient(135deg, rgba(79, 70, 229, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)"
                  : "rgba(79, 70, 229, 0.05)",
                color: colors.textPrimary,
                border: "1px solid rgba(79, 70, 229, 0.1)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, rgba(79, 70, 229, 0.2) 0%, rgba(124, 58, 237, 0.15) 100%)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(79, 70, 229, 0.15)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <Avatar
                src={user?.profileImage}
                alt={user?.name}
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1.5,
                  border: "2px solid rgba(79, 70, 229, 0.2)",
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                  boxShadow: "0 4px 12px rgba(79, 70, 229, 0.2)",
                }}
              >
                {!user?.profileImage && (user?.name?.[0] || "")}
              </Avatar>
              <Typography
                variant="body1"
                noWrap
                sx={{
                  mr: 0.5,
                  fontWeight: 600,
                  maxWidth: { xs: "100px", sm: "150px" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name || "User"}
              </Typography>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      {renderProfileMenu}
      {renderNotifications}

      {/* Global Sign-Out Overlay */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 999, // Ensure it's above the drawer, popovers, and appbar
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backdropFilter: "blur(8px)",
          background: "rgba(15, 23, 42, 0.8)", // Sleek dark overlay matching your gradients
        }}
        open={loggingOut}
      >
        <CircularProgress color="inherit" size={48} thickness={4} />
        <Typography variant="h6" fontWeight={500} letterSpacing={1}>
          Signing out securely...
        </Typography>
      </Backdrop>
    </>
  );
};

export default AppBarComponent;
